import React from 'react';
import { Calendar, Shield, Users, FileText, Globe, Lock, Eye, Database } from 'lucide-react';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-dpn-pink/5 via-white to-dpn-olive/5">
      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Title Section */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-dpn-olive/10 rounded-full">
              <Shield className="h-12 w-12 text-dpn-olive" />
            </div>
          </div>
          <h1 className="text-4xl font-runtime font-bold text-gray-900 mb-4">
            Privacy Policy
          </h1>
          <p className="text-lg text-gray-600 font-lucy-rose max-w-2xl mx-auto">
            Learn how we collect, use, and protect your personal information on Durga Puja Network.
          </p>
        </div>

        {/* Privacy Policy Content */}
        <div className="bg-white rounded-2xl shadow-xl p-8 space-y-8">
          
          {/* 1. Introduction */}
          <section>
            <div className="flex items-center mb-4">
              <div className="p-2 bg-dpn-olive/10 rounded-lg mr-3">
                <Shield className="h-5 w-5 text-dpn-olive" />
              </div>
              <h2 className="text-2xl font-runtime font-bold text-gray-900">1. Introduction</h2>
            </div>
            <div className="space-y-4 text-gray-700 font-lucy-rose leading-relaxed">
              <h3 className="font-runtime font-semibold text-lg text-gray-800">Welcome to Durga Puja Network</h3>
              <p>
                Mind's Workstation India Pvt. Ltd is the licensed owner of the brand Durga Puja Network and the website www.durgapuja.network ("The Site").
              </p>
              <p>
                MIND'S WORKSTATION INDIA PVT. LTD respects your privacy. This Privacy Policy provides succinctly the manner your data is collected and used by MIND'S WORKSTATION INDIA PVT. LTD on the Site. As a visitor to the Site/Customer you are advised to please read the Privacy Policy carefully. By accessing the services provided by the Site, you agree to the collection and use of your data by MIND'S WORKSTATION INDIA PVT. LTD in the manner provided in this Privacy Policy.
              </p>
              <p>
                These Terms of Service ("Terms", "Terms of Service") govern your use of our website located at www.durgapuja.network (together or individually "Service") operated by MIND'S WORKSTATION INDIA PVT. LTD.
              </p>
            </div>
          </section>

          {/* 2. Definitions */}
          <section>
            <div className="flex items-center mb-4">
              <div className="p-2 bg-dpn-pink/10 rounded-lg mr-3">
                <FileText className="h-5 w-5 text-dpn-pink" />
              </div>
              <h2 className="text-2xl font-runtime font-bold text-gray-900">2. Definitions</h2>
            </div>
            <div className="space-y-4 text-gray-700 font-lucy-rose leading-relaxed">
              <p><strong>SERVICE</strong> means the www.durgapuja.network website operated by Mind's Workstation India Pvt. Ltd.</p>
              <p><strong>PERSONAL DATA</strong> means data about a living individual who can be identified from those data (or from those and other information either in our possession or likely to come into our possession).</p>
              <p><strong>USAGE DATA</strong> is data collected automatically either generated by the use of Service or from Service infrastructure itself (for example, the duration of a page visit).</p>
              <p><strong>COOKIES</strong> are small files stored on your device (computer or mobile device).</p>
              <p><strong>DATA CONTROLLER</strong> means a natural or legal person who (either alone or jointly or in common with other persons) determines the purposes for which and the manner in which any personal data are, or are to be, processed. For the purpose of this Privacy Policy, we are a Data Controller of your data.</p>
              <p><strong>DATA PROCESSORS (OR SERVICE PROVIDERS)</strong> means any natural or legal person who processes the data on behalf of the Data Controller. We may use the services of various Service Providers in order to process your data more effectively.</p>
              <p><strong>DATA SUBJECT</strong> is any living individual who is the subject of Personal Data.</p>
              <p><strong>THE USER</strong> is the individual using our Service. The User corresponds to the Data Subject, who is the subject of Personal Data.</p>
            </div>
          </section>

          {/* 3. Information Collection and Use */}
          <section>
            <div className="flex items-center mb-4">
              <div className="p-2 bg-dpn-light-green/10 rounded-lg mr-3">
                <Database className="h-5 w-5 text-dpn-light-green" />
              </div>
              <h2 className="text-2xl font-runtime font-bold text-gray-900">3. Information Collection and Use</h2>
            </div>
            <div className="space-y-4 text-gray-700 font-lucy-rose leading-relaxed">
              <p>
                We collect several different types of information for various purposes to provide and improve our Service to you.
              </p>
            </div>
          </section>

          {/* 4. Types of Data Collected */}
          <section>
            <div className="flex items-center mb-4">
              <div className="p-2 bg-dpn-raspberry/10 rounded-lg mr-3">
                <Database className="h-5 w-5 text-dpn-raspberry" />
              </div>
              <h2 className="text-2xl font-runtime font-bold text-gray-900">4. Types of Data Collected</h2>
            </div>
            <div className="space-y-4 text-gray-700 font-lucy-rose leading-relaxed">
              <h3 className="font-runtime font-semibold text-lg text-gray-800">Personal Data</h3>
              <p>
                While using our Service, we may ask you to provide us with certain personally identifiable information that can be used to contact or identify you ("Personal Data"). Personally identifiable information may include, but is not limited to:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Email address</li>
                <li>First name and last name</li>
                <li>Phone number</li>
                <li>Address, Country, State, Province, ZIP/Postal code, City</li>
                <li>Cookies and Usage Data</li>
              </ul>
              <p>
                We may use your Personal Data to contact you with newsletters, marketing or promotional materials and other information that may be of interest to you. You may opt out of receiving any, or all, of these communications from us by following the unsubscribe link.
              </p>

              <h3 className="font-runtime font-semibold text-lg text-gray-800 mt-6">Usage Data</h3>
              <p>
                We may also collect information that your browser sends whenever you visit our Service or when you access Service by or through any device ("Usage Data").
              </p>
              <p>
                This Usage Data may include information such as your computer's Internet Protocol address (e.g. IP address), browser type, browser version, the pages of our Service that you visit, the time and date of your visit, the time spent on those pages, unique device identifiers and other diagnostic data.
              </p>
              <p>
                When you access Service with a device, this Usage Data may include information such as the type of device you use, your device unique ID, the IP address of your device, your device operating system, the type of Internet browser you use, unique device identifiers and other diagnostic data.
              </p>

              <h3 className="font-runtime font-semibold text-lg text-gray-800 mt-6">Location Data</h3>
              <p>
                We may use and store information about your location if you give us permission to do so ("Location Data"). We use this data to provide features of our Service, to improve and customize our Service.
              </p>
              <p>
                You can enable or disable location services when you use our Service at any time by way of your device settings.
              </p>

              <h3 className="font-runtime font-semibold text-lg text-gray-800 mt-6">Tracking Cookies Data</h3>
              <p>
                We use cookies and similar tracking technologies to track the activity on our Service and we hold certain information.
              </p>
              <p>
                Cookies are files with a small amount of data which may include an anonymous unique identifier. Cookies are sent to your browser from a website and stored on your device. Other tracking technologies are also used such as beacons, tags and scripts to collect and track information and to improve and analyse our Service.
              </p>
              <p>
                You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our Service.
              </p>
              <p>Examples of Cookies we use:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li><strong>Session Cookies:</strong> We use Session Cookies to operate our Service.</li>
                <li><strong>Preference Cookies:</strong> We use Preference Cookies to remember your preferences and various settings.</li>
                <li><strong>Security Cookies:</strong> We use Security Cookies for security purposes.</li>
                <li><strong>Advertising Cookies:</strong> Advertising Cookies are used to serve you with advertisements that may be relevant to you and your interests.</li>
              </ul>

              <h3 className="font-runtime font-semibold text-lg text-gray-800 mt-6">Other Data</h3>
              <p>
                While using our Service, we may also collect the following information: sex, age, date of birth, place of birth, passport details, citizenship, registration at place of residence and actual address, telephone number.
              </p>
            </div>
          </section>

          {/* 5. Use of Data */}
          <section>
            <div className="flex items-center mb-4">
              <div className="p-2 bg-dpn-olive/10 rounded-lg mr-3">
                <Eye className="h-5 w-5 text-dpn-olive" />
              </div>
              <h2 className="text-2xl font-runtime font-bold text-gray-900">5. Use of Data</h2>
            </div>
            <div className="space-y-4 text-gray-700 font-lucy-rose leading-relaxed">
              <p>Mind's Workstation India Pvt. Ltd uses the collected data for various purposes:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>to provide and maintain our Service;</li>
                <li>to notify you about changes to our Service;</li>
                <li>to allow you to participate in interactive features of our Service when you choose to do so;</li>
                <li>to provide customer support;</li>
                <li>to gather analysis or valuable information so that we can improve our Service;</li>
                <li>to monitor the usage of our Service;</li>
                <li>to detect, prevent and address technical issues;</li>
                <li>to fulfil any other purpose for which you provide it;</li>
                <li>to carry out our obligations and enforce our rights arising from any contracts entered into between you and us, including for billing and collection;</li>
                <li>to provide you with notices about your account and/or subscription, including expiration and renewal notices, email-instructions, etc.;</li>
                <li>to provide you with news, special offers and general information about other goods, services and events which we offer that are similar to those that you have already purchased or enquired about unless you have opted not to receive such information;</li>
                <li>in any other way we may describe when you provide the information;</li>
                <li>for any other purpose with your consent.</li>
              </ul>
            </div>
          </section>

          {/* 6. Retention of Data */}
          <section>
            <div className="flex items-center mb-4">
              <div className="p-2 bg-dpn-pink/10 rounded-lg mr-3">
                <Calendar className="h-5 w-5 text-dpn-pink" />
              </div>
              <h2 className="text-2xl font-runtime font-bold text-gray-900">6. Retention of Data</h2>
            </div>
            <div className="space-y-4 text-gray-700 font-lucy-rose leading-relaxed">
              <p>
                We will retain your Personal Data only for as long as is necessary for the purposes set out in this Privacy Policy. We will retain and use your Personal Data to the extent necessary to comply with our legal obligations (for example, if we are required to retain your data to comply with applicable laws), resolve disputes, and enforce our legal agreements and policies.
              </p>
              <p>
                We will also retain Usage Data for internal analysis purposes. Usage Data is generally retained for a shorter period, except when this data is used to strengthen the security or to improve the functionality of our Service, or we are legally obligated to retain this data for longer time periods.
              </p>
            </div>
          </section>

          {/* 7. Transfer of Data */}
          <section>
            <div className="flex items-center mb-4">
              <div className="p-2 bg-dpn-light-green/10 rounded-lg mr-3">
                <Globe className="h-5 w-5 text-dpn-light-green" />
              </div>
              <h2 className="text-2xl font-runtime font-bold text-gray-900">7. Transfer of Data</h2>
            </div>
            <div className="space-y-4 text-gray-700 font-lucy-rose leading-relaxed">
              <p>
                Your information, including Personal Data, may be transferred to – and maintained on – computers located outside of your state, province, country or other governmental jurisdiction where the data protection laws may differ from those of your jurisdiction.
              </p>
              <p>
                If you are located outside India and choose to provide information to us, please note that we transfer the data, including Personal Data, to India and process it there.
              </p>
              <p>
                Your consent to this Privacy Policy followed by your submission of such information represents your agreement to that transfer.
              </p>
              <p>
                Mind's Workstation India Pvt. Ltd will take all the steps reasonably necessary to ensure that your data is treated securely and in accordance with this Privacy Policy and no transfer of your Personal Data will take place to an organisation or a country unless there are adequate controls in place including the security of your data and other personal information.
              </p>
            </div>
          </section>

          {/* 8. Disclosure of Data */}
          <section>
            <div className="flex items-center mb-4">
              <div className="p-2 bg-dpn-raspberry/10 rounded-lg mr-3">
                <Users className="h-5 w-5 text-dpn-raspberry" />
              </div>
              <h2 className="text-2xl font-runtime font-bold text-gray-900">8. Disclosure of Data</h2>
            </div>
            <div className="space-y-4 text-gray-700 font-lucy-rose leading-relaxed">
              <p>We may disclose personal information that we collect, or you provide:</p>
              <h3 className="font-runtime font-semibold text-lg text-gray-800">Disclosure for Law Enforcement.</h3>
              <p>Under certain circumstances, we may be required to disclose your Personal Data if required to do so by law or in response to valid requests by public authorities of Govt of India.</p>
              
              <h3 className="font-runtime font-semibold text-lg text-gray-800">Business Transaction.</h3>
              <p>If we or our subsidiaries are involved in a merger, acquisition or asset sale, your Personal Data may be transferred.</p>
              
              <h3 className="font-runtime font-semibold text-lg text-gray-800">Other cases.</h3>
              <p>We may disclose your information also:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>to our subsidiaries and affiliates;</li>
                <li>to contractors, service providers, and other third parties we use to support our business;</li>
                <li>to fulfill the purpose for which you provide it;</li>
                <li>for the purpose of including your company's logo on our website;</li>
                <li>for any other purpose disclosed by us when you provide the information;</li>
                <li>with your consent in any other cases;</li>
                <li>if we believe disclosure is necessary or appropriate to protect the rights, property, or safety of the Company, our customers, or others.</li>
              </ul>
            </div>
          </section>

          {/* 9. Security of Data */}
          <section>
            <div className="flex items-center mb-4">
              <div className="p-2 bg-dpn-olive/10 rounded-lg mr-3">
                <Lock className="h-5 w-5 text-dpn-olive" />
              </div>
              <h2 className="text-2xl font-runtime font-bold text-gray-900">9. Security of Data</h2>
            </div>
            <div className="space-y-4 text-gray-700 font-lucy-rose leading-relaxed">
              <p>
                The security of your data is important to us but remember that no method of transmission over the Internet or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your Personal Data, we cannot guarantee its absolute security.
              </p>
            </div>
          </section>

          {/* 10. GDPR Rights */}
          <section>
            <div className="flex items-center mb-4">
              <div className="p-2 bg-dpn-pink/10 rounded-lg mr-3">
                <Shield className="h-5 w-5 text-dpn-pink" />
              </div>
              <h2 className="text-2xl font-runtime font-bold text-gray-900">10. Your Data Protection Rights Under General Data Protection Regulation (GDPR)</h2>
            </div>
            <div className="space-y-4 text-gray-700 font-lucy-rose leading-relaxed">
              <p>
                If you are a resident of the European Union (EU) and European Economic Area (EEA), you have certain data protection rights, covered by GDPR.
              </p>
              <p>
                We aim to take reasonable steps to allow you to correct, amend, delete, or limit the use of your Personal Data.
              </p>
              <p>
                If you wish to be informed what Personal Data we hold about you and if you want it to be removed from our systems, please email us at hello@DurgaPuja.Network.
              </p>
              <p>In certain circumstances, you have the following data protection rights:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>the right to access, update or to delete the information we have on you;</li>
                <li>the right of rectification. You have the right to have your information rectified if that information is inaccurate or incomplete;</li>
                <li>the right to object. You have the right to object to our processing of your Personal Data;</li>
                <li>the right of restriction. You have the right to request that we restrict the processing of your personal information;</li>
                <li>the right to data portability. You have the right to be provided with a copy of your Personal Data in a structured, machine-readable and commonly used format;</li>
                <li>the right to withdraw consent. You also have the right to withdraw your consent at any time where we rely on your consent to process your personal information;</li>
              </ul>
              <p>
                Please note that we may ask you to verify your identity before responding to such requests. Please note, we may not able to provide Service without some necessary data.
              </p>
              <p>
                You have the right to complain to a Data Protection Authority about our collection and use of your Personal Data. For more information, please contact your local data protection authority in the European Economic Area (EEA).
              </p>
            </div>
          </section>

          {/* 11. Your Data Protection Rights */}
          <section>
            <div className="flex items-center mb-4">
              <div className="p-2 bg-dpn-light-green/10 rounded-lg mr-3">
                <Users className="h-5 w-5 text-dpn-light-green" />
              </div>
              <h2 className="text-2xl font-runtime font-bold text-gray-900">11. Your Data Protection Rights</h2>
            </div>
            <div className="space-y-4 text-gray-700 font-lucy-rose leading-relaxed">
              <p>
                You are entitled to learn what data we collect about you, ask to delete your data and not to sell (share) it. To exercise your data protection rights, you can make certain requests and ask us:
              </p>
              <h3 className="font-runtime font-semibold text-lg text-gray-800">What personal information we have about you.</h3>
              <p>If you make this request, we will return to you:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>The categories of personal information we have collected about you.</li>
                <li>The categories of sources from which we collect your personal information.</li>
                <li>The business or commercial purpose for collecting or selling your personal information.</li>
                <li>The categories of third parties with whom we share personal information.</li>
                <li>The specific pieces of personal information we have collected about you.</li>
                <li>A list of categories of personal information that we have sold, along with the category of any other company we sold it to. If we have not sold your personal information, we will inform you of that fact.</li>
                <li>A list of categories of personal information that we have disclosed for a business purpose, along with the category of any other company we shared it with.</li>
              </ul>
              <p>
                Please note, you are entitled to ask us to provide you with this information up to two times in a rolling twelve-month period. When you make this request, the information provided may be limited to the personal information we collected about you in the previous 12 months.
              </p>
              
              <h3 className="font-runtime font-semibold text-lg text-gray-800">To delete your personal information.</h3>
              <p>
                If you make this request, we will delete the personal information we hold about you as of the date of your request from our records and direct any service providers to do the same. In some cases, deletion may be accomplished through de-identification of the information. If you choose to delete your personal information, you may not be able to use certain functions that require your personal information to operate.
              </p>
              
              <h3 className="font-runtime font-semibold text-lg text-gray-800">To stop selling your personal information.</h3>
              <p>
                We don't sell or rent your personal information to any third parties for any purpose. We do not sell your personal information for monetary consideration. However, under some circumstances, a transfer of personal information to a third party, or within our family of companies, without monetary consideration may be considered a "sale" under California law. You are the only owner of your Personal Data and can request disclosure or deletion at any time.
              </p>
              <p>
                If you submit a request to stop selling your personal information, we will stop making such transfers.
              </p>
              <p>
                Please note, if you ask us to delete or stop selling your data, it may impact your experience with us, and you may not be able to participate in certain programs or membership services which require the usage of your personal information to function. But in no circumstances, we will discriminate against you for exercising your rights.
              </p>
              <p>
                To exercise your data protection rights described above, please send your request(s) by email: hello@DurgaPuja.Network.
              </p>
            </div>
          </section>

          {/* 12-21. Remaining Sections */}
          <section>
            <div className="flex items-center mb-4">
              <div className="p-2 bg-dpn-raspberry/10 rounded-lg mr-3">
                <Globe className="h-5 w-5 text-dpn-raspberry" />
              </div>
              <h2 className="text-2xl font-runtime font-bold text-gray-900">12. Service Providers</h2>
            </div>
            <div className="space-y-4 text-gray-700 font-lucy-rose leading-relaxed">
              <p>
                We may employ third party companies and individuals to facilitate our Service ("Service Providers"), provide Service on our behalf, perform Service-related services or assist us in analysing how our Service is used.
              </p>
              <p>
                These third parties have access to your Personal Data only to perform these tasks on our behalf and are obligated not to disclose or use it for any other purpose.
              </p>
            </div>
          </section>

          <section>
            <div className="flex items-center mb-4">
              <div className="p-2 bg-dpn-olive/10 rounded-lg mr-3">
                <Eye className="h-5 w-5 text-dpn-olive" />
              </div>
              <h2 className="text-2xl font-runtime font-bold text-gray-900">13. Analytics</h2>
            </div>
            <div className="space-y-4 text-gray-700 font-lucy-rose leading-relaxed">
              <p>We may use third-party Service Providers to monitor and analyze the use of our Service.</p>
            </div>
          </section>

          <section>
            <div className="flex items-center mb-4">
              <div className="p-2 bg-dpn-pink/10 rounded-lg mr-3">
                <FileText className="h-5 w-5 text-dpn-pink" />
              </div>
              <h2 className="text-2xl font-runtime font-bold text-gray-900">14. CI/CD tools</h2>
            </div>
            <div className="space-y-4 text-gray-700 font-lucy-rose leading-relaxed">
              <p>We may use third-party Service Providers to automate the development process of our Service.</p>
            </div>
          </section>

          <section>
            <div className="flex items-center mb-4">
              <div className="p-2 bg-dpn-light-green/10 rounded-lg mr-3">
                <Database className="h-5 w-5 text-dpn-light-green" />
              </div>
              <h2 className="text-2xl font-runtime font-bold text-gray-900">15. Advertising</h2>
            </div>
            <div className="space-y-4 text-gray-700 font-lucy-rose leading-relaxed">
              <p>We may use third-party Service Providers to show advertisements to you to help support and maintain our Service.</p>
            </div>
          </section>

          <section>
            <div className="flex items-center mb-4">
              <div className="p-2 bg-dpn-raspberry/10 rounded-lg mr-3">
                <Eye className="h-5 w-5 text-dpn-raspberry" />
              </div>
              <h2 className="text-2xl font-runtime font-bold text-gray-900">16. Behavioural Remarketing</h2>
            </div>
            <div className="space-y-4 text-gray-700 font-lucy-rose leading-relaxed">
              <p>We may use remarketing services to advertise on third party websites to you after you visited our Service. We and our third-party vendors use cookies to inform, optimise and serve ads based on your past visits to our Service.</p>
            </div>
          </section>

          <section>
            <div className="flex items-center mb-4">
              <div className="p-2 bg-dpn-olive/10 rounded-lg mr-3">
                <Lock className="h-5 w-5 text-dpn-olive" />
              </div>
              <h2 className="text-2xl font-runtime font-bold text-gray-900">17. Payments</h2>
            </div>
            <div className="space-y-4 text-gray-700 font-lucy-rose leading-relaxed">
              <p>
                We may provide paid products and/or services within Service. In that case, we use third-party services for payment processing (e.g. payment processors).
              </p>
              <p>
                We will not store or collect your payment card details. That information is provided directly to our third-party payment processors whose use of your personal information is governed by their Privacy Policy. These payment processors adhere to the standards set by PCI-DSS as managed by the PCI Security Standards Council, which is a joint effort of brands like Visa, Mastercard, American Express and Discover. PCI-DSS requirements help ensure the secure handling of payment information.
              </p>
            </div>
          </section>

          <section>
            <div className="flex items-center mb-4">
              <div className="p-2 bg-dpn-pink/10 rounded-lg mr-3">
                <Globe className="h-5 w-5 text-dpn-pink" />
              </div>
              <h2 className="text-2xl font-runtime font-bold text-gray-900">18. Links to Other Sites</h2>
            </div>
            <div className="space-y-4 text-gray-700 font-lucy-rose leading-relaxed">
              <p>
                Our Service may contain links to other sites that are not operated by us. If you click a third party link, you will be directed to that third party's site. We strongly advise you to review the Privacy Policy of every site you visit.
              </p>
              <p>
                We have no control over and assume no responsibility for the content, privacy policies or practices of any third party sites or services.
              </p>
            </div>
          </section>

          <section>
            <div className="flex items-center mb-4">
              <div className="p-2 bg-dpn-light-green/10 rounded-lg mr-3">
                <Users className="h-5 w-5 text-dpn-light-green" />
              </div>
              <h2 className="text-2xl font-runtime font-bold text-gray-900">19. Children's Privacy</h2>
            </div>
            <div className="space-y-4 text-gray-700 font-lucy-rose leading-relaxed">
              <p>
                Our Services are not intended for use by children under the age of 18 ("Child" or "Children").
              </p>
              <p>
                We do not knowingly collect personally identifiable information from Children under 18. If you become aware that a Child has provided us with Personal Data, please contact us. If we become aware that we have collected Personal Data from Children without verification of parental consent, we take steps to remove that information from our servers.
              </p>
            </div>
          </section>

          <section>
            <div className="flex items-center mb-4">
              <div className="p-2 bg-dpn-raspberry/10 rounded-lg mr-3">
                <Calendar className="h-5 w-5 text-dpn-raspberry" />
              </div>
              <h2 className="text-2xl font-runtime font-bold text-gray-900">20. Changes to This Privacy Policy</h2>
            </div>
            <div className="space-y-4 text-gray-700 font-lucy-rose leading-relaxed">
              <p>
                We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page.
              </p>
              <p>
                We will let you know via email and/or a prominent notice on our Service, prior to the change becoming effective and update "effective date" at the top of this Privacy Policy.
              </p>
              <p>
                You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
              </p>
            </div>
          </section>

          {/* Contact Us */}
          <section className="border-t pt-8">
            <div className="flex items-center mb-4">
              <div className="p-2 bg-dpn-olive/10 rounded-lg mr-3">
                <FileText className="h-5 w-5 text-dpn-olive" />
              </div>
              <h2 className="text-2xl font-runtime font-bold text-gray-900">21. Contact Us</h2>
            </div>
            <div className="space-y-4 text-gray-700 font-lucy-rose leading-relaxed">
              <p>
                If you have any questions about this Privacy Policy, please contact us by email: hello@DurgaPuja.Network
              </p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;